import { CURR_ENV } from '@/core/constants/appConfig/environment';
// import { Logger } from '@/shared/logger/logger';

import {
  CookiesSetValueParams,
  EBrowserStorageType,
  SmGetValueFunction,
  SmGetValueFunctionParams,
  SmGetValueFunctionReturnValue,
  SmSetValueFunction,
  SmSetValueFunctionParams,
} from './browser_storage_api_types';
import { CookieLibrary } from './cookie_lib/cookie_lib';
import { GlobalState } from './global_state/global_state';

/**
 * Provides a set of APIs to interact with browser storage.
 */
export class BrowserStorageApi {
  /**
   * Sets a value in the specified storage provider.
   *
   * @param params - The parameters including the storage provider, key, value, and options.
   * @returns An object indicating whether the operation was successful.
   */
  public static set_value = ({
    item_key,
    storage_provider,
    ...rest
  }: SmSetValueFunctionParams): { success: boolean } => {
    const handlers: Record<EBrowserStorageType, SmSetValueFunction> = {
      [EBrowserStorageType.COOKIES]: BrowserStorageApi.create_cookie,
      [EBrowserStorageType.GLOBAL_STATE]:
        BrowserStorageApi.insert_into_global_state,
      [EBrowserStorageType.LOCALSTORAGE]:
        BrowserStorageApi.insert_into_local_storage,
      [EBrowserStorageType.SESSIONSTORAGE]:
        BrowserStorageApi.insert_into_session_storage,
    };

    item_key = `${CURR_ENV}_${item_key}`;
    const success = handlers[storage_provider]({
      item_key,
      ...rest,
      storage_provider,
    });

    if (!success) {
      // Logger.info(
      //   '[ERROR] Failed to insert the item_key value pair into storage provider',
      // );
    }

    return success;
  };

  /**
   * Gets a value from the specified storage provider.
   *
   * @param params - The parameters including the storage provider and key.
   * @returns The value from the storage provider, or null if not found.
   */
  public static get_value = ({
    default: defaultValue,
    item_key,
    storage_provider,
  }: SmGetValueFunctionParams): SmGetValueFunctionReturnValue => {
    const handlers: Record<EBrowserStorageType, SmGetValueFunction> = {
      [EBrowserStorageType.COOKIES]: BrowserStorageApi.get_cookie,
      [EBrowserStorageType.GLOBAL_STATE]:
        BrowserStorageApi.get_from_global_state,
      [EBrowserStorageType.LOCALSTORAGE]:
        BrowserStorageApi.get_from_local_storage,
      [EBrowserStorageType.SESSIONSTORAGE]:
        BrowserStorageApi.get_from_session_storage,
    };
    item_key = `${CURR_ENV}_${item_key}`;
    let result;
    try {
      result = handlers[storage_provider]({
        default: defaultValue,
        item_key,
        storage_provider,
      });
    } catch (error: any) {
      // Logger.info(
      //   '[ERROR] Failed to get the item_key value pair from storage provider',
      // );
      return {
        err_msg: error?.message || error || 'UNKNOWN_ERROR',
        item_value: defaultValue,
        success: false,
      };
    }

    return result.success
      ? result
      : { err_msg: result.err_msg, item_value: defaultValue, success: false };
  };

  /**
   * Inserts a key-value pair into local storage.
   */
  public static insert_into_local_storage: SmSetValueFunction = ({
    item_key,
    item_value,
  }) => {
    localStorage.setItem(item_key, item_value);
    return { success: true };
  };

  /**
   * Gets a value from local storage.
   */
  public static get_from_local_storage: SmGetValueFunction = ({
    default: defaultValue,
    item_key,
  }) => {
    const item_value = localStorage.getItem(item_key) || defaultValue;
    return { item_value, success: true };
  };

  /**
   * Inserts a key-value pair into session storage.
   */
  public static insert_into_session_storage: SmSetValueFunction = ({
    item_key,
    item_value,
  }) => {
    sessionStorage.setItem(item_key, item_value);
    return { success: true };
  };

  /**
   * Gets a value from session storage.
   */
  public static get_from_session_storage: SmGetValueFunction = ({
    default: defaultValue,
    item_key,
  }) => {
    const item_value = sessionStorage.getItem(item_key) || defaultValue;
    return { item_value, success: true };
  };

  /**
   * Inserts a key-value pair into the global state.
   */
  public static insert_into_global_state: SmSetValueFunction = ({
    item_key,
    item_value,
  }) => {
    void GlobalState.Instance.setGlobalVariable<string>(item_key, item_value);
    return { success: true };
  };

  /**
   * Gets a value from the global state.
   */
  public static get_from_global_state: SmGetValueFunction = ({
    default: defaultValue,
    item_key,
  }) => {
    const item_value = GlobalState.Instance.getGlobalVariable<string>(
      item_key,
      defaultValue,
    );
    return { item_value, success: true };
  };

  /**
   * Creates a cookie with the specified parameters.
   */
  public static create_cookie: SmSetValueFunction = params => {
    const { item_key, item_value, provider_options } =
      params as CookiesSetValueParams;
    if (provider_options && Object.keys(provider_options).length === 0) {
      CookieLibrary.set(item_key, item_value);
    } else {
      CookieLibrary.set(item_key, item_value, provider_options);
    }
    return { success: true };
  };

  /**
   * Gets a cookie value with the specified key.
   */
  public static get_cookie: SmGetValueFunction = ({
    default: defaultValue,
    item_key,
  }) => {
    const item_value = CookieLibrary.get(item_key) || defaultValue;
    return { item_value, success: true };
  };
}
